import axios from 'axios';

// Base API configuration
const API_BASE_URL = 'http://localhost:8080/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/auth/signin', credentials),
  register: (userData) => api.post('/auth/signup', userData),
  refreshToken: () => api.post('/auth/refresh'),
};

// User Management API
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (userData) => api.put('/users/profile', userData),
  getAllUsers: () => api.get('/users'),
  getUserById: (id) => api.get(`/users/${id}`),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  getUsersByRole: (role) => api.get(`/users/role/${role}`),
  getUsersByStatus: (status) => api.get(`/users/status/${status}`),
  getTotalUserCount: () => api.get('/users/stats/total'),
  getActiveUserCount: () => api.get('/users/stats/active'),
};

// AI Agents API
export const aiAgentAPI = {
  getUserAgents: () => api.get('/ai-agents'),
  getAllAgents: () => api.get('/ai-agents/all'),
  getAgentById: (id) => api.get(`/ai-agents/${id}`),
  createAgent: (agentData) => api.post('/ai-agents', agentData),
  updateAgent: (id, agentData) => api.put(`/ai-agents/${id}`, agentData),
  updateAgentStatus: (id, status) => api.put(`/ai-agents/${id}/status?status=${status}`),
  deleteAgent: (id) => api.delete(`/ai-agents/${id}`),
  getAgentsByStatus: (status) => api.get(`/ai-agents/status/${status}`),
  getAgentsByType: (type) => api.get(`/ai-agents/type/${type}`),
  getTotalAgentCount: () => api.get('/ai-agents/stats/total'),
  getActiveAgentCount: () => api.get('/ai-agents/stats/active'),
};

// Workflows API
export const workflowAPI = {
  getUserWorkflows: () => api.get('/workflows'),
  getAllWorkflows: () => api.get('/workflows/all'),
  getWorkflowById: (id) => api.get(`/workflows/${id}`),
  createWorkflow: (workflowData) => api.post('/workflows', workflowData),
  updateWorkflow: (id, workflowData) => api.put(`/workflows/${id}`, workflowData),
  updateWorkflowStatus: (id, status) => api.put(`/workflows/${id}/status?status=${status}`),
  executeWorkflow: (id) => api.post(`/workflows/${id}/execute`),
  deleteWorkflow: (id) => api.delete(`/workflows/${id}`),
  getWorkflowsByStatus: (status) => api.get(`/workflows/status/${status}`),
  getActiveWorkflows: () => api.get('/workflows/active'),
  getTotalWorkflowCount: () => api.get('/workflows/stats/total'),
  getActiveWorkflowCount: () => api.get('/workflows/stats/active'),
};

// Services API
export const serviceAPI = {
  getPublicServices: () => api.get('/services/public'),
  getServiceById: (id) => api.get(`/services/public/${id}`),
  getServicesByCategory: (category) => api.get(`/services/public/category/${category}`),
  searchServices: (name) => api.get(`/services/public/search?name=${name}`),
  getPopularServices: () => api.get('/services/public/popular'),
  getServicesByPriceRange: (minPrice, maxPrice) => 
    api.get(`/services/public/price-range?minPrice=${minPrice}&maxPrice=${maxPrice}`),
  deployService: (id) => api.post(`/services/${id}/deploy`),
  
  // Admin endpoints
  getAllServices: () => api.get('/services/admin/all'),
  createService: (serviceData) => api.post('/services/admin', serviceData),
  updateService: (id, serviceData) => api.put(`/services/admin/${id}`, serviceData),
  deleteService: (id) => api.delete(`/services/admin/${id}`),
  getTotalServiceCount: () => api.get('/services/admin/stats/total'),
  getServiceCountByCategory: (category) => api.get(`/services/admin/stats/category/${category}`),
};

// Dashboard & Analytics API
export const dashboardAPI = {
  getUserStats: () => api.get('/dashboard/stats'),
  getAdminStats: () => api.get('/dashboard/admin/stats'),
};

// API Keys API
export const apiKeyAPI = {
  getUserApiKeys: () => api.get('/api-keys'),
  getApiKeyById: (id) => api.get(`/api-keys/${id}`),
  createApiKey: (keyData) => api.post('/api-keys', keyData),
  updateApiKey: (id, keyData) => api.put(`/api-keys/${id}`, keyData),
  updateApiKeyStatus: (id, status) => api.put(`/api-keys/${id}/status?status=${status}`),
  regenerateApiKey: (id) => api.post(`/api-keys/${id}/regenerate`),
  deleteApiKey: (id) => api.delete(`/api-keys/${id}`),
  validateApiKey: (keyValue) => api.post('/api-keys/validate', { keyValue }),
  
  // Admin endpoints
  getExpiredApiKeys: () => api.get('/api-keys/admin/expired'),
  cleanupExpiredApiKeys: () => api.post('/api-keys/admin/cleanup-expired'),
  getTotalApiKeyCount: () => api.get('/api-keys/admin/stats/total'),
  getActiveApiKeyCount: () => api.get('/api-keys/admin/stats/active'),
};

// Utility functions
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('jwt_token', token);
  } else {
    localStorage.removeItem('jwt_token');
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('jwt_token');
};

export const setUser = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
};

export const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

export const logout = () => {
  localStorage.removeItem('jwt_token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

export default api;